import { useEffect, useRef, useState } from 'react';

import { isSafari } from '../utils/navigator';

export const DEFAULT_AUDIO_VOLUME = isSafari ? 0.5 : 0.2;

const useAudio = (url?: string, autoPlay = true, loop = true) => {
  const audioRef = useRef<HTMLAudioElement>();
  const [isPlaying, setIsPlaying] = useState<boolean>();
  const [volume, setVolume] = useState(
    window.location.hostname === 'localhost' || isSafari ? 0 : DEFAULT_AUDIO_VOLUME,
  );

  useEffect(() => {
    if (isSafari) {
      setIsPlaying(false);
    }
  }, []);

  const playOrPauseAudio = (shouldPlay: boolean) => {
    if (audioRef.current) {
      if (shouldPlay) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
    }
  };

  useEffect(() => {
    // Support changing url to change the song
    if (url) {
      // if we already have an existing audio playing. stop it before creating a new one
      if (audioRef.current) {
        playOrPauseAudio(false);
      }

      const audio = new Audio(url);
      audio.autoplay = volume === 0 ? false : autoPlay;
      audio.loop = loop;
      audio.volume = volume;

      if (volume !== 0) {
        // Binds audio callbacks to set state in a failproof way
        audio.onplaying = () => {
          // called whenever audio has actually started to play
          setIsPlaying(true);
        };

        audio.onpause = () => {
          // called whenever audio has actually been paused
          setIsPlaying(false);
        };
      }

      audioRef.current = audio;
    }

    if (!url && audioRef.current) {
      // setting the url to undefined while an existing audio exists in state should stop playing music
      playOrPauseAudio(false);
      audioRef.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, loop]);

  useEffect(() => {
    if (!audioRef.current) {
      // Dont do anything when we have no audio object
      return;
    }
    try {
      // always adjust the volume
      // request to play if volume was 0 and is now not zero
      if (audioRef.current.volume === 0 && !isPlaying && volume > 0) {
        playOrPauseAudio(true);
      }
      // try catch to handle browsers that are strict with user permissions (safari etc)
      audioRef.current.volume = volume || 0;
      if (isSafari && volume !== 0) {
        audioRef.current.play();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('No permissions to set volume yet', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volume]);

  useEffect(() => {
    // When unmounting the component. Make sure to stop playing music if music is playing
    return () => {
      if (audioRef.current) {
        playOrPauseAudio(false);
      }
    };
  }, []);

  return {
    playing: isPlaying,
    playOrPauseAudio,
    audio: audioRef.current,
    setVolume,
    volume,
  };
};

export default useAudio;
